import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { post } from '../shared/apiClient';
import { clearAuth, getUserIdFromAccessToken, setInMemoryToken } from '../shared/auth';
import { migrateLegacyFlowSession } from '../features/flow/session';

interface TokenResponse {
  access_token: string;
}

interface AuthState {
  accessToken: string | null;
  userId: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  applyToken: (token: string) => void;
}

const AuthContext = createContext<AuthState>({
  accessToken: null,
  userId: null,
  isLoading: true,
  isAuthenticated: false,
  login: async () => {},
  logout: async () => {},
  applyToken: () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const userId = useMemo(() => getUserIdFromAccessToken(accessToken), [accessToken]);

  const applyToken = useCallback((token: string) => {
    setInMemoryToken(token);
    setAccessToken(token);
    const uid = getUserIdFromAccessToken(token);
    if (uid) migrateLegacyFlowSession(uid);
  }, []);

  useEffect(() => {
    post<undefined, TokenResponse>('/auth/refresh', undefined, { credentials: 'include' })
      .then((r) => applyToken(r.data.access_token))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [applyToken]);

  const login = useCallback(async (email: string, password: string) => {
    const r = await post<{ email: string; password: string }, TokenResponse>(
      '/auth/login',
      { email, password },
      { credentials: 'include' },
    );
    applyToken(r.data.access_token);
  }, [applyToken]);

  const logout = useCallback(async () => {
    await post('/auth/logout', undefined, { credentials: 'include' }).catch(() => {});
    clearAuth();
    setAccessToken(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ accessToken, userId, isLoading, isAuthenticated: !!accessToken, login, logout, applyToken }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthState => useContext(AuthContext);

export default AuthContext;
